<app-page-shell [breadcrumb]="breadcrumb" width="wide">
  <header class="mx-auto max-w-3xl px-6 pt-12 pb-14 text-center md:pt-16 md:pb-20">
    <p class="mb-4 eyebrow">{{ i18n + '.hero.eyebrow' | transloco }}</p>
    <h1 class="hero-title font-display text-4xl font-bold leading-[1.06] text-foreground md:text-[3.25rem]">
      <span>{{ i18n + '.hero.titlePrefix' | transloco }}</span>
      <span class="hero-title-accent italic font-medium text-primary">{{ i18n + '.hero.titleAccent' | transloco }}</span>
    </h1>
    <div class="mx-auto mt-6 flex max-w-2xl flex-col gap-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
      <p>{{ i18n + '.hero.description1' | transloco }}</p>
      <p>{{ i18n + '.hero.description2' | transloco }}</p>
    </div>
  </header>

  <!-- Busca -->
  <div
    class="mx-auto mt-10 flex max-w-xl items-center gap-2 rounded-full border border-border bg-card px-4 py-2.5 transition focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/30"
  >
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-muted-foreground">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
    <input
      #searchInput
      type="text"
      role="searchbox"
      [value]="query()"
      (input)="onSearch(searchInput.value)"
      [attr.placeholder]="i18n + '.searchPlaceholder' | transloco"
      [attr.aria-label]="i18n + '.searchPlaceholder' | transloco"
      class="input-unstyled min-w-0 flex-1 appearance-none border-0 bg-transparent p-0 text-sm text-foreground shadow-none outline-none ring-0 placeholder:text-muted-foreground/70 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:shadow-none"
    />
    @if (query()) {
      <button
        type="button"
        (click)="onSearch(''); searchInput.value = ''"
        [attr.aria-label]="i18n + '.clearSearch' | transloco"
        class="rounded-full p-1 text-muted-foreground transition hover:text-foreground"
      >
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5">
          <path d="M18 6 6 18" /><path d="m6 6 12 12" />
        </svg>
      </button>
    }
  </div>

  <!-- Toolbar -->
  <div class="mx-auto mt-12 flex max-w-6xl items-center justify-between border-b border-border pb-3">
    <p class="text-sm text-muted-foreground">
      <span class="font-semibold text-foreground">{{ filtered().length }}</span>
      {{
        (filtered().length === 1
          ? i18n + '.questionSingular'
          : i18n + '.questionPlural'
        ) | transloco
      }}
    </p>
    <div class="flex items-center gap-2 text-xs text-muted-foreground">
      <span>{{ i18n + '.sortLabel' | transloco }}</span>
      <app-editorial-select
        [options]="sortOptions()"
        [value]="sort()"
        [ariaLabel]="i18n + '.sortLabel' | transloco"
        align="right"
        (valueChange)="onSort($event)"
      />
    </div>
  </div>

  <!-- Grade -->
  @if (loading()) {
    <div class="mx-auto mt-6 grid max-w-6xl gap-px overflow-hidden rounded-lg border border-border bg-border md:grid-cols-2 lg:grid-cols-3">
      @for (i of [1, 2, 3, 4, 5, 6]; track i) {
        <div class="h-36 animate-pulse bg-background"></div>
      }
    </div>
  } @else {
    <div class="mx-auto mt-6 grid max-w-6xl gap-px overflow-hidden rounded-lg border border-border bg-border md:grid-cols-2 lg:grid-cols-3">
      @if (filtered().length === 0) {
        <div class="col-span-full bg-background p-10 text-center">
          <p class="font-display text-base font-semibold text-foreground">
            {{ i18n + '.emptyTitle' | transloco }}
          </p>
          <p class="mt-1 text-sm text-muted-foreground">
            {{ i18n + '.emptyDescription' | transloco }}
          </p>
        </div>
      } @else {
        @for (card of filtered(); track card.id) {
          <a
            [routerLink]="'/igualdade-racial/' + card.perguntaId | localizedRouterLink"
            [queryParams]="card.queryParams"
            class="group flex h-full flex-col justify-between gap-6 bg-background p-6 transition hover:bg-primary/[0.04]"
          >
            <div>
              <p class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                {{ card.platform }}
              </p>
              <h3 class="mt-2 font-display text-base font-semibold leading-snug text-foreground transition-colors group-hover:text-primary">
                {{ card.short }}
              </h3>
            </div>
            <div class="flex items-center justify-between text-xs text-muted-foreground">
              <span class="inline-flex items-center gap-1 tabular-nums">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3">
                  <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                {{ card.views }}
              </span>
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5 -translate-x-1 opacity-0 transition group-hover:translate-x-0 group-hover:opacity-100 group-hover:text-primary">
                <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
              </svg>
            </div>
          </a>
        }
      }
    </div>
  }

  <app-promo-banner
    eyebrowKey="pages.racialEquality.promo.eyebrow"
    titlePrefixKey="pages.racialEquality.promo.titlePrefix"
    titleAccentKey="pages.racialEquality.promo.titleAccent"
    descriptionKey="pages.racialEquality.promo.description"
    ctaLabelKey="pages.racialEquality.promo.cta"
    ctaPath="/nuvem-perguntas"
  />
</app-page-shell>
